import { useState, type FormEvent } from "react";
import { inquiryApi } from "@/lib/api";
import { type FormStatus } from "@/lib/types";
import ScrollReveal from "@/components/ScrollReveal";

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState<string>("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const data = new FormData(formEl);

    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }
    data.delete("website_alt");

    const message = String(data.get("message") || "");
    if (message.length > 500) {
      setStatus("error");
      setFormError("문의 내용은 500자 이내로 작성해 주세요.");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const name = String(data.get("name") || "");
      const phone = String(data.get("phone") || "");
      const email = String(data.get("email") || "");
      const company = String(data.get("company") || "");
      const service = String(data.get("service") || "원목칩 파쇄");
      const screen = String(data.get("screen") || "40×40");
      const site = String(data.get("site") || "");

      await inquiryApi.create({
        name,
        phone,
        email,
        company,
        service,
        screen,
        site,
        message,
      });

      setStatus("success");
      formEl.reset();
    } catch (err: unknown) {
      setStatus("error");
      const serverMsg = err instanceof Error ? err.message : "";
      if (serverMsg && /spam/i.test(serverMsg)) {
        setFormError("입력 내용이 스팸으로 판단되었습니다. 다시 확인해 주세요.");
      } else if (serverMsg) {
        setFormError(serverMsg);
      } else {
        setFormError("문의 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      }
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-background-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <ScrollReveal direction="up" delay={100}>
              <div className="inline-flex items-center gap-2 text-primary-700 text-xs tracking-[0.3em] uppercase font-medium mb-5">
                <span className="w-8 h-px bg-primary-500"></span>
                Get in Touch
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight">
                파쇄 상담,
                <br />
                <span className="text-primary-700">지금 문의</span>하세요.
              </h2>
              <p className="mt-6 text-foreground-700 text-base md:text-lg leading-relaxed">
                현장 위치, 물량, 원하시는 칩 사이즈만 알려주시면
                담당자가 24시간 안에 연락드립니다.
              </p>
            </ScrollReveal>

            <div className="mt-10 space-y-5">
              {[
                {
                  icon: "ri-map-pin-line",
                  label: "본사 위치",
                  value: "경상남도 양산시 명동 990-9, 990-11",
                },
                {
                  icon: "ri-phone-line",
                  label: "대표 전화",
                  value: "010-8079-0428",
                },
                {
                  icon: "ri-mail-line",
                  label: "이메일",
                  value: "manager@jigugnc.co.kr",
                },
                {
                  icon: "ri-time-line",
                  label: "상담 가능 시간",
                  value: "평일 08:00 - 19:00 · 현장에 따라 유동",
                },
              ].map((c, i) => (
                <ScrollReveal key={c.label} direction="left" delay={200 + i * 100}>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-md bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${c.icon} text-primary-700 text-lg w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <div>
                      <div className="text-foreground-500 text-xs tracking-wide uppercase">
                        {c.label}
                      </div>
                      <div className="mt-1 text-foreground-950 text-base font-medium">
                        {c.value}
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            <ScrollReveal direction="up" delay={400}>
              <div className="mt-10 rounded-lg overflow-hidden border border-background-200/70 h-64">
                <iframe
                  title="우공이산 위치 지도"
                  src="https://www.google.com/maps?q=경상남도+양산시+명동+990-9&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                ></iframe>
              </div>
            </ScrollReveal>
          </div>

          <div className="lg:col-span-7">
            <ScrollReveal direction="right" delay={200}>
              <div className="rounded-lg bg-background-100/60 border border-background-200/70 p-6 md:p-10">
                <form
                  data-readdy-form
                  id="ugis-contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-2">
                        이름 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        required
                        name="name"
                        type="text"
                        placeholder="담당자 성함"
                        className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-2">
                        연락처 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        required
                        name="phone"
                        type="tel"
                        placeholder="010-8079-0428"
                        className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-2">
                        이메일
                      </label>
                      <input
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-2">
                        회사 / 소속
                      </label>
                      <input
                        name="company"
                        type="text"
                        placeholder="예) OO 산림조합"
                        className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-2">
                        원하시는 작업
                      </label>
                      <select
                        name="service"
                        defaultValue="원목칩 파쇄"
                        className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 pr-8"
                      >
                        <option value="원목칩 파쇄">원목칩 파쇄</option>
                        <option value="가지칩 파쇄">가지칩 파쇄</option>
                        <option value="뿌리 파쇄">뿌리 파쇄</option>
                        <option value="자원 재활용 컨설팅">자원 재활용 컨설팅</option>
                        <option value="기타">기타</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-2">
                        스크린 사이즈
                      </label>
                      <select
                        name="screen"
                        defaultValue="40×40"
                        className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 pr-8"
                      >
                        <option value="40×40">40 × 40</option>
                        <option value="50×50">50 × 50</option>
                        <option value="60×60">60 × 60</option>
                        <option value="70×70">70 × 70</option>
                        <option value="상담 후 결정">상담 후 결정</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">
                      현장 위치 / 예상 물량
                    </label>
                    <input
                      name="site"
                      type="text"
                      placeholder="예) 경북 예천, 원목 약 30톤"
                      className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">
                      문의 내용 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      required
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder="현장 조건, 원하시는 일정, 궁금한 점을 자유롭게 남겨주세요. (최대 500자)"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400 resize-none"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="hp-field-decoy"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="flex items-start gap-3 pt-2">
                    <input
                      id="agree"
                      name="agree"
                      type="checkbox"
                      required
                      className="mt-1 w-4 h-4 accent-primary-500 cursor-pointer"
                    />
                    <label htmlFor="agree" className="text-sm text-foreground-700 cursor-pointer">
                      개인정보 수집 및 이용에 동의합니다. (문의 상담 목적)
                    </label>
                  </div>

                  {status === "success" && (
                    <div className="p-4 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm flex items-start gap-2">
                      <i className="ri-check-double-line text-lg w-5 h-5 flex items-center justify-center"></i>
                      문의가 정상적으로 접수되었습니다. 담당자가 곧 연락드릴게요.
                    </div>
                  )}
                  {status === "error" && (
                    <div className="p-4 rounded-md bg-accent-100 border border-accent-300 text-accent-900 text-sm flex items-start gap-2">
                      <i className="ri-error-warning-line text-lg w-5 h-5 flex items-center justify-center"></i>
                      {formError || "문의 전송에 실패했습니다. 잠시 후 다시 시도해 주세요."}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full md:w-auto px-10 py-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                        전송 중...
                      </>
                    ) : (
                      <>
                        상담 문의 보내기
                        <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}